
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { authorizeUserServerSide } from 'api/firebaseAdmin';
import { useLang } from 'context/langContext';
import { Namespace } from 'enums/namespaces';
import { basicSEO } from 'helpers';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import useTranslation from 'next-translate/useTranslation';
import Head from 'next/head';
import React, { useCallback, useMemo } from 'react';
import { StaticPathParams } from 'typings/staticPaths';
import Layout from '../../components/Layout';
import styles from './index.module.scss';

interface Props {
  hideHeaderAndFooter?: boolean;
  hideLinks?: boolean;
  countryCode: string;
  forceLocale?: string;
}

const PrivacyPolicy = ({
  hideHeaderAndFooter = false,
  hideLinks = false,
  countryCode,
  forceLocale,
}: Props): JSX.Element => {
  const { t } = useTranslation(Namespace.Common);
  const { locale: _locale } = useLang();

  const locale = useMemo(() => {
    return forceLocale ?? _locale;
  }, [_locale, forceLocale]);

  const policyEn = () => {
    return (
      <>
        <p className={styles.updated}>Updated Dec 9, 2020</p>
        <h1>Privacy Policy</h1>
        <h2>Personal Data Controller:</h2>
        <p>
          <span>
            Dramox s.r.o., with its registered office at Bucharova 2928/14a, Stodůlky, 158 00 Prague 5, Aspira Business
            Centre, a company registered in the Commercial Register kept by the Municipal Court in Prague, Section C,
            File 332337
            <br />
          </span>
        </p>
        <span>Contact&nbsp;e-mail: </span>
        <a href="mailto:support@dramox.tv">support@dramox.tv</a>
        <span>
          .<br />
          <br />
        </span>

        <h2>Data Processing Principles</h2>
        <ul>
          <li>
            <span>
              The objective of this website is to summarise basic information on the principles of personal data
              processing within the DRAMOX service, which we observe and have adopted in order to comply with Regulation{' '}
            </span>
            <span>(EU) 2016/679 of the European Parliament and of the Council (hereinafter the “</span>
            <b>GDPR</b>
            <span>
              ”), effective in the territory of the Czech Republic from 25 May 2018, and Act No. 110/2019 Coll., on
              personal data processing, as amended.&nbsp;
            </span>
          </li>
          <li>
            <span>
              In accordance with the GDPR, we comply with the following principles in processing of personal data:&nbsp;
            </span>
            <ul>
              <li>
                <em>
                  <span>Lawfulness, fairness and transparency</span>
                </em>
                <span>
                  – We perform processing only for legitimate reasons (e.g. statutory duty, performance of a contract,
                  protection of our interests, protection of interests of third parties, or consent granted by the data
                  subject). We perform processing in a transparent manner and we always inform you of how your personal
                  data are handled, who has access to them and what are your rights in connection with the processing.
                </span>
              </li>
              <li>
                <em>Purpose limitation</em> – We collect personal data only for specified, explicit and legitimate
                purposes (see above).&nbsp;
              </li>
              <li>
                <em>Data minimisation</em> – We process personal data only to the extent and within the scope necessary
                in relation to the given purpose.
              </li>
              <li>
                <em>Accuracy</em> - We process only up-to-date personal data.
              </li>
              <li>
                <em>Storage limitation</em> – We do not keep personal data for a period longer than necessary and
                permitted by law.
              </li>
              <li>
                <em>Integrity, confidentiality</em> – We have adopted sufficient technical and organisational measures
                to protect personal data against accidental or unlawful destruction, loss, alteration or unauthorised
                disclosure of or access to the personal data transmitted, stored or otherwise processed.
              </li>
              <li>
                Responsibility – We are able to prove compliance with the principles pursuant to paragraphs 1 to 6 at
                any time.
              </li>
            </ul>
          </li>
        </ul>

        <h2>Legal Grounds for Processing</h2>
        <ul>
          <li>
            Most of the personal data are processed for the purpose of performing the contract entered into with you. If
            we did not have these data, we would not be able to agree with you on the details of our service, process
            the payment made and enter into the contract with you. We do not request any other data from you and if you
            do provide us with them, we believe you are doing so voluntarily.
          </li>
        </ul>

        <h2>Extent of Personal Data Processing</h2>
        <ul>
          <li>
            Within our activities, we process especially the following personal data:
            <ul>
              <li>
                <span>name, surname (for the purposes of entering into and performing the contract);</span>
              </li>
              <li>
                <span>e-mail address (for the purposes of communication);</span>
              </li>
              <li>
                <span>payment card information (for the purpose of payment processing).</span>
              </li>
            </ul>
          </li>
          <li>
            <span>
              If we perform processing that is not aimed at fulfilling the statutory duties, this constitutes personal
              data processing for which we need an explicit, freely given, specific and informed consent. Such consent
              is absolutely voluntary and may be withdrawn at any time, or other rights may be exercised as described
              specifically in the written consent. If we require such consent from you, we will ask you first and will
              not process the data without this consent.
            </span>
          </li>
        </ul>

        <h2>Security</h2>
        <ul>
          <li>
            We have also adopted the necessary measures to ensure security of the personal data being processed in both
            physical and electronic form. These measures include, among other things, rules for using information
            systems, ensuring that only authorised persons have access to the data and that it is at any time possible
            to verify when, by whom and for what reason the personal data were recorded or otherwise processed. We are,
            of course, able to prevent unauthorised access to data carriers, especially by setting passwords, access
            rights, encryption, and other technical and organisational measures.
          </li>
        </ul>

        <h2>Transfers of personal Data</h2>
        <ul>
          <li>
            We transfer personal data to third parties only in cases prescribed by law (mandatory reports to
            governmental authorities) or to the necessary extent to selected suppliers providing certain services for
            us, such as bookkeeping, administration of information technologies (IT) or payment gates used for
            processing of payments for services. We have clearly set contractual relationships with all such persons and
            all suppliers comply with the necessary rules for personal data processing within the scope and parameters
            required by the GDPR.
          </li>
          <li>
            We do not transfer personal data abroad and if this would be necessary, we would ask for your consent in
            advance.
          </li>
        </ul>

        <h2>Rights of data subjects</h2>
        <ul>
          <li>
            In relation to the processing of your personal data, you have a number of options you can use at any time.
            This includes, in particular, the following rights:
            <ul>
              <li>Right of access to personal data;</li>
              <li>Right to rectification of personal data;</li>
              <li>Right to erasure of personal data (right to be forgotten);</li>
              <li>Right to restriction of processing;</li>
              <li>Right to data portability;</li>
              <li>Right to object against processing;</li>
              <li>
                Right to withdraw consent to personal data processing if the processing is performed on the basis of the
                consent;
              </li>
              <li>Right to lodge a complaint with a supervisory authority.</li>
            </ul>
          </li>
          <li>
            In case of breach of security of personal data in spite of all the described measures, we have a system in
            place of reporting any security incidents. In case of any breach of security of personal data , we proceed
            in accordance with the GDPR in order to minimise possible damage and we submit the relevant reports to the
            Office for Personal Data Protection (<a href="http://www.uoou.cz">www.uoou.cz</a>).
          </li>
          <li>
            <span>
              Should you have any questions regarding the scope and manner of personal data processing, please do not
              hesitate to contact us at any time by e-mail at <a href="mailto:support@dramox.tv">support@dramox.tv</a>.
            </span>
          </li>
        </ul>
      </>
    );
  };

  const policyCs = () => {
    return (
      <>
        <p className={styles.updated}>Aktualizováno 16.4.2025</p>
        <h1>Ochrana soukromí</h1>
        <h2>Správce osobních údajů</h2>
        <p>
          Dramox s.r.o., se sídlem na adrese Bucharova 2928/14a, Stodůlky, 158 00 Praha 5, Aspira Business Centre,
          společnost zapsaná v obchodním rejstříku vedeném Městským soudem v Praze, oddíl C, vložka 332337
        </p>
        <p>
          Kontaktní e-mail: <a href="mailto:jeviste@dramox.cz">jeviste@dramox.cz</a>.
        </p>
        <h2>Zásady zpracování</h2>
        <p>
          Cílem této stránky je shrnutí základních informací o zásadách zpracování osobních údajů služby DRAMOX, kterými
          se řídíme a které jsme přijali za účelem zajištění souladu s nařízením parlamentu a Rady EU 2016/679 (dále jen
          „GDPR“), které je na území ČR účinné od 25.5.2018 a zákona 110/2019 Sb., o zpracování osobních údajů, ve znění
          pozdějších předpisů.
        </p>
        <p>V souladu s GDPR zpracováváme osobní údaje dle těchto zásad:</p>
        <ul>
          <li>
            Zákonnost, korektnost a transparent – Provádíme zpracování jen v případě, kdy k tomu existuje legitimní
            důvod (např. zákonná povinnost, plnění smlouvy, ochrana našich zájmů, ochrana zájmů 3. osob, nebo udělený
            souhlas subjektu údajů). Provádíme zpracování transparentně a informuje Vás vždy o tom, jak je s osobními
            údaji nakládáno, kdo k nim má přístup, jaká máte v souvislosti se zpracováním práva.
          </li>
          <li>
            Účelové omezení – Shromažďujeme osobní údaje jen pro konkrétní, výslovně vyjádřené a legitimní účely (viz
            výše).
          </li>
          <li>
            Minimalizace údajů – Zpracování osobních údajů provádíme pouze v míře a rozsahu, který je nezbytný ve vztahu
            k danému účelu.
          </li>
          <li>Přesnost – Zpracováváme pouze aktuální osobní údaje.</li>
          <li>Omezení uložení – Nedržíme osobní údaje po dobu delší, než je nezbytné a zákonné.</li>
          <li>
            Integrita, důvěrnost – Zavedli jsme dostatečná technická a organizační opatření k ochraně osobních údajů
            před náhodným nebo protiprávním zničením, ztrátou, změnou nebo neoprávněnému poskytnutí nebo zpřístupnění
            přenášených, uložených nebo jinak zpracovávaných osobních údajů.
          </li>
          <li>Odpovědnost – Jsme kdykoli schopni doložit soulad dodržování zásad dle bodů 1. – 6.</li>
        </ul>
        <h2>Právní titul zpracování</h2>
        <ol>
          <li>
            <strong>Plnění smlouvy</strong>
            <br />
            Většinu osobních údajů zpracováváme za účelem plnění smlouvy s Vámi. Pokud bychom tyto údaje neměli, nebyli
            bychom schopni se s Vámi domluvit na detailech naší služby, zpracovat provedenou platbu a uzavřít s Vámi
            smlouvu. Žádné jiné údaje od Vás nepožadujeme a pokud nám je sami poskytnete, věříme, že tak činíte
            dobrovolně.
            <br />
            &nbsp;
          </li>
          <li>
            <strong>Oprávněný zájem</strong>
            <br />
            Našim stávajícím uživatelům jsme oprávněni zasílat informace o našich novinkách, akcích a nových záznamech
            dostupných na našem portálu. Ke sdělení těchto informací jsme na základě našeho oprávněného zájmu oprávněni
            zasílat sdělení prostřednictvím e-mailu, který nám uživatelé poskytli. V rámci každé takto odeslané zprávy
            je současně možnost se z přijímání dalších podobných zpráv odhlásit.
            <br />
            &nbsp;
          </li>
          <li>
            <strong>Souhlas</strong>
            <br />V případě, že nám poskytnete své osobní údaje, například e-mailovou adresu a současně nám udělíte
            souhlas se zasíláním obchodních sdělení, budeme oprávněni Vám zasílat obchodní sdělení e-mailem. V rámci
            každé takto odeslané zprávy je současně možnost se z přijímání dalších podobných zpráv odhlásit.
          </li>
        </ol>
        <h2>Rozsah zpracování osobních údajů</h2>
        <p>V rámci naší činnosti zpracováváme zejména tyto osobní údaje: </p>
        <ul>
          <li>jméno, příjmení (pro účely uzavření a plnění smlouvy)</li>
          <li>e-mailovou adresu (pro účely komunikace a v omezených případech pro zasílání obchodních sdělení)</li>
          <li>informace o platební kartě (za účelem zpracování platby)</li>
        </ul>
        <p>
          V případě, že provádíme zpracování, jehož účelem není splnění zákonem předepsaných povinností, jedná se o
          zpracování osobních údajů, ke kterému potřebujeme výslovný, svobodný, konkrétní a informovaný souhlas.
          Poskytnutí takového souhlasu je zcela dobrovolné a souhlas lze kdykoli odvolat či využít další práva, která
          jsou přesně v písemném souhlasu popsána. Kdykoli bychom takový souhlas po Vás požadovali, zeptáme se a bez
          tohoto souhlasu údaje zpracovávat nebudeme.
        </p>
        <h2>Doba uložení</h2>
        <p>
          Veškeré osobní údaje zpracováváme pouze po dobu nezbytně nutnou k naplnění účelu, pro který jsme tyto údaje od
          uživatelů získali. Jakmile pomine důvod ke zpracování Vašich údajů, ukončíme jejich další zpracování a po
          uplynutí předepsané doby tyto údaje z našich systémů odstraníme.
        </p>
        <p>
          Důvodem pro ukončení zpracování Vašich údajů je především ukončení Vašeho uživatelského účtu nebo skončení
          Vašeho předplatného. S ohledem na povinnosti, které nám ukládají zákony, zejména v oblasti vedení účetnictví,
          jsme povinni uchovávat údaje o přijatých platbách, tedy včetně Vašich identifikačních údajů, po dobu nejdéle
          tří let po skončení Vašeho předplatného.
        </p>
        <p>
          V případě údajů využívaných pro účely zasílání obchodních sdělení Vaše údaje odstraníme obratem poté, kdy nám
          sdělíte, že si nepřejete obchodní sdělení dostávat, nebo odvoláte dříve udělený souhlas. V takovém případě
          budeme pouze uchovávat minimální nutné informace k tomu, abychom Váš e-mail vyřadili z naší databáze a nebyl v
          budoucnu znovu využit.
        </p>
        <h2>Zabezpečení</h2>
        <p>
          Přijali jsme také nezbytná opatření k zajištění bezpečnosti zpracovávaných osobních údajů jak v jejich
          fyzické, tak elektronické podobě. Mezi tato opatření patří zejména stanovení pravidel pro práci s informačními
          systémy, zajištění, aby k údajům měly přístup pouze oprávněné osoby a aby bylo možné vždy ověřit, kdy, kým a z
          jakého důvodu byly osobní údaje zaznamenány nebo jinak zpracovány. Samozřejmě jsme schopni zamezit
          neoprávněnému přístupu k datovým nosičům, zejména prostřednictvím nastavení hesel, přístupových práv,
          šifrování, a dalších technicko- organizačních opatření.
        </p>
        <h2>Předávání osobních údajů</h2>
        <p>
          Osobní údaje předáváme třetím osobám pouze v zákonem předepsaných případech (povinná hlášení orgánům státní
          správy) nebo v nezbytném rozsahu vybraným dodavatelům, kteří pro nás zajišťují některé služby, jako například
          vedení účetnictví nebo správu informačních technologií (IT) , platební brány užívané ke zpracování plateb za
          službu nebo rozesílku obchodních sdělení prostřednictvím služby{' '}
          <a
            target="_blank"
            href="https://ecomail.cz/?utm_source=Google&utm_medium=Search&utm_campaign=search_brand&gad_source=1&gbraid=0AAAAADoBiP0NiCtXbgOqdDQasVWZt7DDO&gclid=Cj0KCQjwqv2_BhC0ARIsAFb5Ac_yag9TYclXnthqBM8cCTkrrz5qC1avffg2bU2CzITdpDLo9TsUYw8aAjSsEALw_wcB"
          >
            Ecomail
          </a>
          . Se všemi takovými osobami máme jasně nastavené smluvní vztahy a všichni dodavatelé splňují nezbytná pravidla
          pro zpracování osobních údajů v rozsahu a parametrech vyžadovaných nařízením GDPR.
        </p>
        <p>
          Předávání osobních údajů do zahraničí neprovádíme, a pokud by to bylo nutné, zeptali bychom se Vás na Váš
          souhlas předem.
        </p>
        <h2>Práva subjektu údajů</h2>
        <p>V souvislosti se zpracováním Vašich osobních údajů máte řadu možností, které můžete kdykoli využít.</p>
        <p>Jde především o tato práva na:</p>
        <ul>
          <li>Přístup k osobním údajům;</li>
          <li>Opravu osobních údajů;</li>
          <li>Výmaz osobních údajů (právo být zapomenut);</li>
          <li>Omezení zpracování;</li>
          <li>Přenositelnost údajů;</li>
          <li>Vznesení námitky proti zpracování;</li>
          <li>Odvolání souhlasu se zpracováním osobních údajů, je-li zpracování prováděno na jeho základě;</li>
          <li>Podání stížnosti u dozorového úřadu.</li>
        </ul>
        <p>
          Pokud by i přes všechna popsaná opatření došlo k úniku Vašich osobních údajů, máme zavedený systém hlášení
          případných bezpečnostních incidentů. V případě úniku jakýchkoli dat postupujeme v souladu s GDPR za účelem
          minimalizace možných škod a provádíme příslušná hlášení Úřadu pro ochranu osobních údajů (
          <a target="_blank" href="http://www.uoou.cz">
            www.uoou.cz
          </a>
          ).
        </p>
        <p>
          V případě, že budete mít k rozsahu a způsobu zpracování osobních údajů na jakékoli dotazy, neváhejte nás
          kdykoli kontaktovat e-mailem na adrese <a href="mailto:jeviste@dramox.cz">jeviste@dramox.cz</a>.
        </p>
        <p>Tato pravidla nabývají účinnosti dnem 1.10. 2020.</p>
      </>
    );
  };

  const policySk = () => {
    return (
      <>
        <p className={styles.updated}>Aktualizované 16.4.2025</p>
        <h1>Zásady ochrany osobných údajov</h1>
        <h2>Správca osobných údajov </h2>
        <p>
          Dramox s.r.o., so sídlom Bucharova 2928/14a, Stodůlky, 158 00 Praha 5, Aspira Business Centre, spoločnosť
          zapísaná v obchodnom registri vedenom Mestským súdom v Prahe, oddiel C, vložka 332337
        </p>
        <p>
          Kontaktný e-mail: <a href="mailto:jeviste@dramox.cz">jeviste@dramox.cz</a>.
        </p>
        <h2>Zásady spracovania</h2>
        <p>
          Účelom tejto stránky je zhrnúť základné informácie o zásadách spracovania osobných údajov spoločnosti DRAMOX,
          ktorými sa riadime a ktoré sme prijali v súlade s nariadením Európskeho parlamentu a Rady EÚ 2016/679 (ďalej
          len „GDPR“), ktoré je v Českej republike účinné od 25. mája 2018, a zákonom č. 110/2019 Z. z. o spracovaní
          osobných údajov v znení neskorších predpisov.
        </p>
        <p>V súlade s GDPR spracúvame osobné údaje podľa týchto zásad:</p>
        <ul>
          <li>
            Zákonnosť, korektnosť a transparentnosť – Osobné údaje spracúvame len vtedy, ak na to existuje legitímny
            dôvod (napr. zákonná povinnosť, plnenie zmluvy, ochrana našich oprávnených záujmov, ochrana záujmov tretích
            osôb alebo udelený súhlas dotknutej osoby). Spracúvanie vykonávame transparentne a vždy Vás informujeme o
            tom, ako s osobnými údajmi nakladáme, kto k nim má prístup a aké práva máte v súvislosti so spracúvaním.
          </li>
          <li>
            Obmedzenie účelu – Osobné údaje zhromažďujeme len na konkrétne, výslovne určené a legitímne účely (pozri
            vyššie).
          </li>
          <li>
            Minimalizácia údajov – Osobné údaje spracúvame len v rozsahu, ktorý je nevyhnutný na dosiahnutie stanoveného
            účelu.
          </li>
          <li>Presnosť – Spracúvame výlučne aktuálne a presné osobné údaje.</li>
          <li>Obmedzenie uchovávania – Osobné údaje neuchovávame dlhšie, ako je nevyhnutné a zákonom povolené.</li>
          <li>
            Integrita a dôvernosť – Zaviedli sme dostatočné technické a organizačné opatrenia na ochranu osobných údajov
            pred náhodným alebo nezákonným zničením, stratou, zmenou alebo neoprávneným zverejnením alebo prístupom k
            prenášaným, uchovávaným alebo inak spracúvaným osobným údajom.
          </li>
          <li>Zodpovednosť – Kedykoľvek vieme preukázať súlad s princípmi uvedenými v bodoch 1 až 6.</li>
        </ul>
        <h2>Právny základ spracúvania</h2>
        <ol>
          <li>
            <strong>Plnenie zmluvy</strong>
            <br />
            Väčšinu osobných údajov spracúvame na účel plnenia zmluvy s Vami. Ak by sme tieto údaje nemali k dispozícii,
            nevedeli by sme sa s vami dohodnúť na detailoch našej služby, spracovať vykonanú platbu ani s vami uzatvoriť
            zmluvu. Nevyžadujeme od Vás žiadne iné údaje a ak nám ich poskytnete dobrovoľne, považujeme to za Váš
            slobodný súhlas.
            <br />
            &nbsp;
          </li>
          <li>
            <strong>Oprávnený záujem</strong>
            <br />
            Našim existujúcim používateľom sme oprávnení zasielať informácie o našich novinkách, akciách a nových
            záznamoch dostupných na našom portáli. Na základe nášho oprávneného záujmu môžeme tieto informácie zasielať
            prostredníctvom e-mailu, ktorý nám používatelia poskytli. V každej takto odoslanej správe je tiež možnosť
            odhlásiť sa z odberu ďalších podobných správ.
            <br />
            &nbsp;
          </li>
          <li>
            <strong>Súhlas</strong>
            <br />V prípade, že nám poskytnete svoje osobné údaje, napríklad e-mailovú adresu, a zároveň nám udelíte
            súhlas so zasielaním obchodných oznámení, budeme oprávnení posielať Vám obchodné oznámenia e-mailom. V
            každej takto zaslanej správe je aj možnosť odhlásenia sa z odberu ďalších podobných správ.
          </li>
        </ol>
        <h2>Rozsah spracúvania osobných údajov</h2>
        <p>V rámci našej činnosti spracúvame najmä tieto osobné údaje:</p>
        <ul>
          <li>meno a priezvisko (na účely uzatvorenia a plnenia zmluvy),</li>
          <li>e-mailová adresa (na účely komunikácie a v obmedzených prípadoch na zasielanie obchodných oznámení),</li>
          <li>informácie o platobnej karte (na účel spracovania platby)</li>
        </ul>
        <p>
          V prípade, že spracúvame osobné údaje, ktorých účelom nie je plnenie zákonných povinností, ide o spracúvanie,
          na ktoré potrebujeme výslovný, slobodný, konkrétny a informovaný súhlas. Poskytnutie takéhoto súhlasu je úplne
          dobrovoľné a súhlas môžete kedykoľvek odvolať alebo využiť ďalšie práva, ktoré sú presne popísané v samotnom
          súhlase. Kedykoľvek od Vás budeme takýto súhlas požadovať, najskôr sa naň výslovne opýtame a bez jeho udelenia
          nebudeme údaje spracúvať.
        </p>
        <h2>Doba uchovávania</h2>
        <p>
          Všetky osobné údaje spracúvame iba po dobu nevyhnutne potrebnú na naplnenie účelu, na ktorý sme ich od
          používateľov získali. Akonáhle dôvod na spracúvanie vašich údajov pominie, ukončíme ich ďalšie spracúvanie a
          po uplynutí zákonnej lehoty údaje zo svojich systémov odstránime.
        </p>
        <p>
          Dôvodom na ukončenie spracúvania vašich údajov je najmä zrušenie Vášho používateľského účtu alebo ukončenie
          Vášho predplatného. Vzhľadom na zákonné povinnosti, najmä v oblasti vedenia účtovníctva, sme povinní uchovávať
          údaje o prijatých platbách – teda aj Vaše identifikačné údaje – po dobu najviac troch rokov od skončenia Vášho
          predplatného.
        </p>
        <p>
          V prípade údajov využívaných na zasielanie obchodných oznámení Vaše údaje odstránime bezodkladne po tom, ako
          nám oznámite, že si neželáte dostávať obchodné oznámenia, alebo po odvolaní predtým udeleného súhlasu. V takom
          prípade uchováme len minimálny rozsah údajov potrebných na to, aby sme Váš e-mail vyradili z databázy a v
          budúcnosti ho opätovne nepoužili.
        </p>
        <h2>Zabezpečenie</h2>
        <p>
          Prijali sme nevyhnutné opatrenia na zabezpečenie bezpečnosti spracúvaných osobných údajov, a to ako v ich
          fyzickej, tak aj elektronickej podobe. Tieto opatrenia zahŕňajú najmä stanovenie pravidiel pre prácu s
          informačnými systémami, zabezpečenie, aby k údajom mali prístup len oprávnené osoby, a aby bolo vždy možné
          overiť, kedy, kým a z akého dôvodu boli osobné údaje zaznamenané alebo inak spracované. Samozrejme, vieme
          zabrániť neoprávnenému prístupu k dátovým nosičom, najmä prostredníctvom nastavenia hesiel, prístupových práv,
          šifrovania a ďalších technicko-organizačných opatrení.
        </p>
        <h2>Predávanie osobných údajov</h2>
        <p>
          Osobné údaje predávame tretím osobám iba v zákonom predpísaných prípadoch (povinné hlásenia orgánom štátnej
          správy) alebo v nevyhnutnom rozsahu vybraným dodávateľom, ktorí pre nás zabezpečujú niektoré služby, ako
          napríklad vedenie účtovníctva, správu informačných technológií (IT), platobné brány používané na spracovanie
          platieb za službu alebo rozosielanie obchodných oznámení prostredníctvom služby{' '}
          <a
            target="_blank"
            href="https://ecomail.cz/?utm_source=Google&utm_medium=Search&utm_campaign=search_brand&gad_source=1&gbraid=0AAAAADoBiP0NiCtXbgOqdDQasVWZt7DDO&gclid=Cj0KCQjwqv2_BhC0ARIsAFb5Ac_yag9TYclXnthqBM8cCTkrrz5qC1avffg2bU2CzITdpDLo9TsUYw8aAjSsEALw_wcB"
          >
            Ecomail
          </a>
          . So všetkými takýmito osobami máme jasne stanovené zmluvné vzťahy a všetci dodávatelia spĺňajú nevyhnutné
          pravidlá pre spracúvanie osobných údajov v rozsahu a parametroch požadovaných nariadením GDPR.
        </p>
        <p>
          Predávanie osobných údajov do zahraničia neuskutočňujeme, a ak by to bolo nevyhnutné, najprv by sme sa Vás
          opýtali na Váš súhlas.
        </p>
        <h2>Práva subjektu údajov</h2>
        <p>
          V súvislosti so spracúvaním Vašich osobných údajov máte množstvo možností, ktoré môžete kedykoľvek využiť. Ide
          najmä o tieto práva:
        </p>
        <ul>
          <li>Prístup k osobným údajom</li>
          <li>Opravu osobných údajov</li>
          <li>Vymazanie osobných údajov (právo byť zabudnutý)</li>
          <li>Obmedzenie spracovania</li>
          <li>Prenositeľnosť údajov</li>
          <li>Vznesenie námietky proti spracovaniu</li>
          <li>Odvolanie súhlasu so spracovaním osobných údajov, ak je spracovanie vykonávané na jeho základe</li>
          <li>Podanie sťažnosti u dozorného orgánu</li>
        </ul>
        <p>
          Ak by napriek všetkým opísaným opatreniam došlo k úniku Vašich osobných údajov, máme zavedený systém hlásenia
          možných bezpečnostných incidentov. V prípade úniku akýchkoľvek údajov postupujeme v súlade s GDPR s cieľom
          minimalizovať možné škody a vykonávame príslušné hlásenia Úradu pre ochranu osobných údajov{' '}
          <a target="_blank" href="http://www.uoou.cz">
            www.uoou.cz
          </a>
          .
        </p>
        <p>
          V prípade, že budete mať akékoľvek otázky ohľadom rozsahu a spôsobu spracovania osobných údajov, neváhajte nás
          kedykoľvek kontaktovať e-mailom na adresu <a href="mailto:jeviste@dramox.cz">jeviste@dramox.cz</a>.
        </p>
        <p>Tieto pravidlá nadobúdajú účinnosť dňa 1.10. 2020.</p>
      </>
    );
  };

  const policeUk = useCallback(
    () => (
      <>
        <p className={styles.updated}>Опубліковано: 11 лютого 2022 року</p>
        <h1>Політика конфіденційності</h1>
        <p>
          Ця Політика конфіденційності (далі – «Політика») містить умови про обробку персональних даних Користувачів
          («Користувач», а також «ви»), яка здійснюється Онлайн-театром Dramox, та є невід’ємною частиною Публічної
          оферти.
        </p>
        <p>
          Володільцем персональних даних є Dramox s.r.o., компанія, що діє під комерційним найменуванням Dramox,
          зареєстрована за наступною адресою: Bucharova 2928/14a, Stodůlky, 158 00 Prague 5, Aspira Business Centre,
          Czech Republic
        </p>
        <p>
          Контактні дані: <a href="mailto:pidtrymka@dramox.tv">pidtrymka@dramox.tv</a>
        </p>
        <p>Відповідно до законодавства про захист персональних даних ця Політика визначає:</p>
        <ul>
          <li>які цілі обробки персональних даних;</li>
          <li>хто здійснює таку обробку;</li>
          <li>які саме персональні дані обробляє Dramox та способи їх обробки; </li>
          <li>які права має Користувач стосовно таких даних;</li>
          <li>чи передаються дані третім особам;</li>
          <li>яких заходів вживає Dramox для захисту персональних даних та строк їх зберігання; </li>
          <li>а також інші деталі обробки персональних даних.</li>
        </ul>
        <p>
          Ми здійснюємо обробку персональних даних прозоро і завжди інформуємо вас про те, як здійснюється процес
          обробки, хто має до них доступ і які права у вас є, з огляду на таку обробку.
        </p>
        <h2>1. Цілі обробки персональних даних</h2>
        <p>
          Ми використовуємо ваші персональні дані, щоб надавати, аналізувати, покращувати та персоналізувати наші
          Послуги та рекламну діяльність, адмініструвати ваш обліковий запис, а також взаємодіяти з вами з інших питань.
        </p>
        <p>Ми обробляємо персональні дані лише в обсязі і межах, необхідних для досягнення цілі такої обробки.</p>
        <h2>2. Хто здійснює обробку персональних даних</h2>
        <p>
          Для того, щоб надавати вам Послуги, ми наймаємо працівників та залучаємо третіх осіб, які можуть отримувати
          доступ до ваших персональних даних, на підставі відповідних угод.
        </p>
        <p>
          Ми передаємо персональні дані третім особам лише у випадках, передбачених законодавством та в необхідному
          обсязі, зокрема у випадках: адміністрування інформаційних технологій; використанні платіжних шлюзів для
          обробки платежів тощо. Ми чітко встановили договірні відносини з усіма такими особами, і всі постачальники
          дотримуються необхідних правил обробки персональних даних у межах обсягу та параметрів.
        </p>
        <p>
          Ми не передаємо персональні дані за кордон, і якщо це буде необхідно, ми будемо просити вашу згоду
          заздалегідь.
        </p>
        <h2>3. Які саме персональні дані обробляє Dramox та способи їх обробки</h2>
        <p>
          Ми отримуємо і зберігаємо будь-яку інформацію, яку ви вводите під час створення облікового запису, зокрема:
          логін, ім'я, прізвище, адресу електронної пошти, пароль, дані щодо обраного способу оплати, інші дані, які
          можуть бути необхідні в процесі надання Послуг та дані, які ви добровільно залишаєте на Вебсайті.
        </p>
        <h2>4. Підстави для обробки персональних даних</h2>
        <p>
          Більшість персональних даних обробляється для виконання умов Договору. Якби у нас не було цих даних, ми не
          змогли б узгодити з вами деталі нашого сервісу, обробити здійснений платіж і укласти з вами Договір. Ми не
          вимагаємо від вас будь-яких інших даних, і якщо ви надасте нам їх, ми вважаємо, що ви робите це добровільно.
        </p>
        <p>
          Підставою для обробки ваших персональних даних є ваша згода. Створюючи обліковий запис ви надаєте згоду на
          використання та обробку ваших персональних даних, відповідно до цієї Політики.
        </p>
        <p>
          Якщо ми виконуємо обробку, що не спрямована на виконання наших договірних обов’язків, це є обробкою
          персональних даних, для якої нам потрібна окрема, чітка, вільна та усвідомлена згода. Така згода є абсолютно
          добровільною і може бути відкликана вами в будь-який час. Перш ніж здійснювати таку обробку персональних
          даних, ми спочатку запитаємо вас і попросимо надати відповідну згоду.
        </p>
        <h2>5. Заходи захисту</h2>
        <p>
          Ми вжили необхідних заходів для забезпечення захисту персональних даних, які обробляються як у фізичній, так і
          в електронній формі. Ці заходи включають, серед іншого, правила використання інформаційних систем, які
          забезпечують доступ до даних лише уповноваженим особам і можливість у будь-який час перевірити, коли, ким і з
          якої причини персональні дані були записані або оброблені. Ми, звичайно, можемо запобігти несанкціонованому
          доступу до носіїв даних, особливо шляхом встановлення паролів, прав доступу, шифрування та інших технічних та
          організаційних заходів.
        </p>
        <p>
          Ми зберігаємо ваші персональні дані впродовж строку існування вашого облікового запису. Якщо ви попросите нас
          видалити свій обліковий запис, ми видалимо усі дані.{' '}
        </p>
        <h2>6. Права Користувача</h2>
        <p>
          Що стосується обробки ваших персональних даних, у вас є ряд опцій, які ви можете використовувати в будь-який
          час. Це включає, зокрема:
        </p>
        <ul>
          <li>Право доступу до персональних даних;</li>
          <li>Право на виправлення персональних даних;</li>
          <li>Право на видалення персональних даних (право на забуття);</li>
          <li>Право на обмеження обробки;</li>
          <li>Право на переносимість даних;</li>
          <li>Право на заперечення проти обробки;</li>
          <li>
            Право на відкликання згоди на обробку персональних даних, якщо обробка здійснюється на підставі згоди;
          </li>
          <li>Право на подання скарги до контролюючого органу.</li>
        </ul>
        <p>
          Ми вживаємо належних заходів безпеки, щоб захистити ваші персональні дані від втрати або знищення, від
          незаконної обробки чи доступу до них. Зокрема, ми створюємо резервні копії ваших даних, а також використовуємо
          безпечний https протокол для комунікації, шифруємо ваші паролі та надаємо доступ до персональних даних
          обмеженому колу осіб та лише у випадку необхідності.
        </p>
        <p>
          У разі порушення безпеки персональних даних, незважаючи на всі описані заходи, у нас є система повідомлення
          про будь-які інциденти.
        </p>
        <p>
          У разі будь-якого порушення безпеки персональних даних ми діємо відповідно до вимог законодавства, щоб
          мінімізувати можливу шкоду.
        </p>
        <p>
          Якщо у вас виникнуть запитання щодо обсягу та способу обробки персональних даних, будь ласка, зв’яжіться з
          нами в будь-який час: <a href="mailto:pidtrymka@dramox.tv">pidtrymka@dramox.tv</a>.
        </p>
        <h2>7. Файли cookies</h2>
        <p>
          Cookies – це невеликі шматочки коду, що залишаються у вашому пристрої після того, як ви відвідали вебсайт і
          дають можливість серверу надавати вам інформацію, індивідуалізовану під ваші потреби, коли ви будете
          використовувати Сервіс наступного разу. Надання вами згоди Dramox на використання файлів cookies робить наш
          Онлайн-театр більш ефективним.
        </p>
      </>
    ),
    [],
  );

  const policyPl = useCallback(() => {
    return (
      <>
        <h1>Polityka Prywatności</h1>
        <h2>Administrator danych osobowych:</h2>
        <p>
          Dramox s.r.o., z siedzibą w Pradze, adres: Bucharova 2928/14a, Stodůlky, 158 00 Praga 5, Aspira Business
          Centre, Republika Czeska, spółka z ograniczoną odpowiedzialnością zarejestrowana w Rejestrze Handlowym
          prowadzonym przez Sąd Miejski w Pradze, dział C, plik 332337 (&bdquo;<strong>Administrator</strong>&ldquo;).
          <br />
          <br />
          <a href="mailto:sufler@dramox.pl">sufler@dramox.pl</a>
        </p>

        <h2>Zasady przetwarzania danych osobowych:</h2>
        <p>
          Celem niniejszej Polityki Prywatności jest przedstawienie podstawowych zasad przetwarzania danych osobowych w
          ramach korzystania z usług platformy DRAMOX znajdującej się pod adresem{' '}
          <a href="https://www.dramox.pl">www.dramox.pl</a> (&bdquo;<strong>Portal</strong>&ldquo;), których
          przestrzegamy i które przyjęliśmy zgodnie z Rozporządzeniem Parlamentu Europejskiego i Rady (UE) 2016/679
          (dalej: &bdquo;<strong>RODO</strong>&ldquo;), obowiązującym na terytorium Unii Europejskiej od 25 maja 2018
          r., a także na podstawie innych przepisów – tj. ustawy nr 110/2019 Sb. o przetwarzaniu danych osobowych z
          późniejszymi zmianami (w odniesieniu do danych osobowych przetwarzanych na terytorium Republiki Czeskiej lub
          mającej zastosowanie zgodnie z jej przepisami), a także zgodnie ze wszelkimi innymi przepisami w zakresie
          ochrony danych osobowych, które będą mieć zastosowanie.
        </p>

        <h2>Podstawy prawne przetwarzania danych osobowych</h2>
        <h3>Dane osobowe są zbierane w przypadku:</h3>

        <ol>
          <li>
            Dane osób korzystających z Portalu, nieposiadających konta na Portalu (niebędących zarejestrowanymi
            użytkownikami), w tym adres IP lub inne identyfikatory oraz informacje gromadzone za pośrednictwem plików
            cookies lub innych podobnych technologii, są przez nas przetwarzane w jednym bądź kilku następujących
            celach:
            <ol>
              <li>
                świadczenia usług drogą elektroniczną w zakresie udostępniana użytkownikom treści zamieszczonych na
                Portalu (podstawa prawna przetwarzania – niezbędność przetwarzania do wykonania umowy – art. 6 ust. 1
                lit. b RODO);
              </li>
              <li>
                obsługi reklamacji (podstawa prawna przetwarzania – niezbędność przetwarzania do wypełnienia obowiązku
                prawnego ciążącego na Administratorze – art. 6 ust. 1 lit. c RODO);
              </li>
              <li>
                ewentualnego ustalenia i dochodzenia roszczeń lub obrony przed roszczeniami (podstawa prawna
                przetwarzania – przetwarzanie jest niezbędne do celów wynikających z prawnie uzasadnionych interesów
                realizowanych przez Administratora – art. 6 ust. 1 lit. f RODO);
              </li>
              <li>
                analitycznych i statystycznych (podstawa prawna przetwarzania – uzasadniony interes Administratora –
                art. 6 ust. 1 lit f RODO – prowadzenie analiz zachowań oraz aktywności użytkowników a także ich
                preferencji mające na celu poprawę jakości oraz adekwatności stosowanych funkcjonalności i świadczonych
                usług).
              </li>
            </ol>
          </li>
          <li>
            Użytkownicy, dokonujący rejestracji na Portalu, proszeni są o podanie danych niezbędnych do utworzenia oraz
            obsługi konta (imię, nazwisko, adres e-mail, dane karty lub inne dane wymagane do przeprocesowania
            płatności). Podanie danych jest dobrowolne, ale wymagane w celu założenia i obsługi konta (niepodanie danych
            skutkuje brakiem możliwości założenia konta). Dane osobowe są przetwarzane przez nas w jednym bądź kilku
            następujących celach:
            <ol>
              <li>
                świadczenia usług związanych z prowadzeniem i obsługą konta na Portalu (podstawa prawna przetwarzania –
                przetwarzanie jest niezbędne do wykonania umowy – art. 6 ust. 1 lit. b RODO);
              </li>
              <li>
                obsługi reklamacji (podstawa prawna przetwarzania – niezbędność przetwarzania do wypełnienia obowiązku
                prawnego ciążącego na Administratorze – art. 6 ust. 1 lit. c RODO);
              </li>
              <li>
                ewentualnego ustalenia i dochodzenia roszczeń lub obrony przed roszczeniami (podstawa prawna
                przetwarzania – przetwarzanie jest niezbędne do celów wynikających z prawnie uzasadnionych interesów
                realizowanych przez Administratora – art. 6 ust. 1 lit. f RODO);
              </li>
              <li>
                analitycznych i statystycznych (podstawa prawna przetwarzania – przetwarzanie jest niezbędne do celów
                wynikających z prawnie uzasadnionych interesów realizowanych przez Administratora – art. 6 ust. 1 lit. f
                RODO – prowadzeniu analiz zachowań oraz aktywności użytkowników a także ich preferencji mający na celu
                poprawę jakości oraz adekwatności stosowanych funkcjonalności i świadczonych usług).
              </li>
            </ol>
          </li>
          <li>
            W przypadku składania zamówienia na usługi oferowane w ramach Portalu, użytkownik podaje dane osobowe
            niezbędne do realizacji zamówienia (imię, nazwisko, adres e-mail, numer telefonu, numer karty płatniczej lub
            inne dane związane z procesowaniem płatnosci). Podanie danych jest dobrowolne, ale wymagane w celu złożenia
            i obsługi zamówienia (niepodanie danych skutkuje brakiem możliwości złożenia zamówienia). Dane osobowe są
            przetwarzane przez nas w jednym bądź kilku następujących celach:
            <ol>
              <li>
                realizacji złożonego zamówienia (podstawa prawna przetwarzania – przetwarzanie jest niezbędne do
                wykonania umowy – art. 6 ust. 1 lit. b RODO);
              </li>
              <li>
                obsługi reklamacji i zwrotów (podstawa prawna przetwarzania – niezbędność przetwarzania do wypełnienia
                obowiązku prawnego ciążącego na Administratorze – art. 6 ust. 1 lit. c RODO);
              </li>
              <li>
                realizacji obowiązków ustawowych ciążących na Administratorze, wynikających w szczególności z przepisów
                podatkowych i przepisów o rachunkowości (podstawa prawna przetwarzania – przetwarzanie jest niezbędne do
                wypełnienia obowiązku prawnego ciążącego na Administratorze – art. 6 ust. 1 lit. c RODO);
              </li>
              <li>
                ewentualnego ustalenia i dochodzenia roszczeń lub obrony przed roszczeniami (podstawa prawna
                przetwarzania – przetwarzanie jest niezbędne do celów wynikających z prawnie uzasadnionych interesów
                realizowanych przez Administratora – art. 6 ust. 1 lit f RODO);
              </li>
              <li>
                analitycznych i statystycznych (podstawa prawna przetwarzania – przetwarzanie jest niezbędne do celów
                wynikających z prawnie uzasadnionych interesów realizowanych przez Administratora – art. 6 ust. 1 lit. f
                RODO – prowadzenie analiz zachowań oraz aktywności użytkowników a także ich preferencji mające na celu
                poprawę jakości oraz adekwatności stosowanych funkcjonalności i świadczonych usług);
              </li>
            </ol>
          </li>
          <li>
            w celach związanych z wysyłką informacji w ramach newslettera i innych wiadomości przygotowanych przez
            Administratora (podstawa prawna przetwarzania – przetwarzanie jest niezbędne do celów wynikających z prawnie
            uzasadnionych interesów realizowanych przez Administratora – art. 6 ust. 1 lit. f RODO – prezentowanie
            użytkownikom aktualnej oferty Portalu oraz utrzymywanie wysokiej jakości obsługi)
          </li>
          <li>
            w celach związanych z badaniem satysfakcji, w szczególności poprzez wysyłanie na adres e-mail komunikacji z
            prośbą o wystawienie opinii (recenzji) lub wypełnienie ankiety satysfakcji (podstawa prawna przetwarzania –
            przetwarzanie jest niezbędne do celów wynikających z prawnie uzasadnionych interesów realizowanych przez
            Administratora – art. 6 ust. 1 lit f RODO – badanie satysfakcji ma na utrzymywaniu wysokiej jakości obsługi
            oraz poziomu zadowolenia Użytkowników z oferowanych produktów i usług).
          </li>
          <li>
            Zgodnie z RODO przestrzegamy następujących zasad przetwarzania danych osobowych:
            <ul>
              <li>
                zgodność z prawem, rzetelność i przejrzystość – przetwarzamy dane wyłącznie z uzasadnionych powodów (np.
                obowiązek wynikający z przepisów prawa, wykonanie umowy, ochrona naszych interesów, ochrona interesów
                osób trzecich lub zgoda wyrażona przez osobę, której dane dotyczą). Przetwarzamy dane osobowe w sposób
                przejrzysty i zawsze informujemy Cię o tym, jak postępujemy z Twoimi danymi, kto ma do nich dostęp oraz
                jakie przysługują Ci prawa w związku z przetwarzaniem.
              </li>
              <li>
                ograniczenie celu przetwarzania — zbieramy dane osobowe wyłącznie w określonych, wyraźnych i prawnie
                uzasadnionych celach.
              </li>
              <li>
                minimalizacja danych – dane osobowe przetwarzamy wyłącznie w zakresie i zakresie niezbędnym w związku z
                danym celem.
              </li>
              <li>dokładność - przetwarzamy tylko aktualne dane osobowe.</li>
              <li>
                ograniczenie przechowywania – nie przechowujemy danych osobowych przez okres dłuższy niż jest to
                konieczne i dozwolone przez prawo.
              </li>
              <li>
                integralność, poufność — przyjęliśmy wystarczające środki techniczne i organizacyjne w celu ochrony
                danych osobowych przed przypadkowym lub niezgodnym z prawem zniszczeniem, utratą, zmianą lub
                nieuprawnionym ujawnieniem lub dostępem do danych osobowych przesyłanych, przechowywanych lub w inny
                sposób przetwarzanych.{' '}
              </li>
              <li>
                odpowiedzialność – w każdej chwili jesteśmy w stanie wykazać przestrzeganie zasad określonych powyżej.
              </li>
            </ul>
          </li>

          <h2>Zakres przetwarzanych danych osobowych</h2>
          <ul>
            <li>
              W ramach naszych działań związanych z Portalem, przetwarzamy następujące dane osobowe:
              <ul>
                <li>imię i nazwisko (w celu zawarcia i wykonywania umowy)</li>
                <li>adres e-mail (w celu komunikacji oraz zawarcia i wykonywania umowy); </li>
                <li>
                  informacje o karcie płatniczej lub inne związane z płatnością (w celu przeprocesowania płatności);
                </li>
                <li>
                  informacje o sposobie korzystania z Portalu - w tym historia aktywności Użytkownika w ramach Portalu -
                  np. wybór tytułów, długość odtwarzania materiałów, statystyka odsłon; identyfikatory urządzeń i cechy
                  tych urządzeń, adres IP, informacje gromadzone przez pliki cookies itp.
                </li>
                <li>
                  inne dane, jeżeli zostaną one przekazane przez użytkownika – np. w zakresie kontaktów z obsługą
                  klienta.
                </li>
              </ul>
            </li>
            <li>
              Jeżeli dokonujemy przetwarzania danych osobowych, które nie ma na celu wypełnienia obowiązków prawnych lub
              nie jest uzasadnione na podstawie obowiązujących przepisów, jest to przetwarzanie danych osobowych, na
              które potrzebujemy wyraźnej, dobrowolnie udzielonej, konkretnej i świadomej zgody. Zgoda taka jest
              całkowicie dobrowolna i może być w każdej chwili wycofana. Możliwe jest też skorzystanie z innych praw
              opisanych szczegółowo w pisemnej zgodzie. Jeśli będziemy wymagać od Ciebie takiej zgody, skierujemy do
              Ciebie zapytanie w celu jej uzyskania i nie będziemy przetwarzać danych osobowych bez uzyskania stosownej
              zgody.
            </li>
          </ul>
        </ol>

        <h2>Okres przetwarzania danych osobowych</h2>
        <p>
          Dane osobowe będą przetwarzane przez nas przez okres niezbędny dla realizacji celów, o których mowa w
          niniejszej Polityce Prywatności.
        </p>
        <p>
          Jeżeli przetwarzanie danych osobowych jest niezbędne do wykonania umowy, dane osobowe mogą być przetwarzane
          tak długo, jak jest to niezbędne do wykonania umowy, a po tym czasie przez okres odpowiadający okresowi
          przedawnienia roszczeń, jakie mogą być podnoszone wobec Administratora oraz jakie może podnosić Administrator.
        </p>
        <p>
          Jeżeli przetwarzanie danych osobowych jest niezbędne do wypełnienia obowiązku prawnego ciążącego na
          Administratorze, dane osobowe mogą być przetwarzane tak długo, jak jest to niezbędne do wywiązania się z
          obowiązków nałożonych przepisami prawa (np. w zakresie rozpatrzenia reklamacji, prowadzenia rachunkowości czy
          wywiązania się z obowiązków podatkowych).
        </p>
        <p>
          Jeżeli przetwarzanie danych osobowych jest niezbędne do celów wynikających z prawnie uzasadnionych interesów
          realizowanych przez Administratora, dane osobowe mogą być przetwarzane do czasu zgłoszenia sprzeciwu wobec
          przetwarzania danych w tym celu (o ile nie występują nadrzędne prawnie uzasadnione podstawy przetwarzania
          takich danych osobowych) oraz przez okres odpowiadający przedawnieniu roszczeń.
        </p>

        <h2>Bezpieczeństwo</h2>
        <ul>
          <li>
            Podejmujemy niezbędne środki w celu zapewnienia bezpieczeństwa przetwarzanych danych osobowych zarówno w
            formie fizycznej, jak i elektronicznej. Do środków tych należą m.in. zasady korzystania z systemów
            informatycznych, zapewnienie, że dostęp do danych mają tylko osoby upoważnione oraz że w każdym momencie
            można sprawdzić, kiedy, przez kogo i z jakiego powodu dane osobowe zostały zapisane lub są w inny sposób
            przetwarzane. Dochowujemy wszelkich starań, aby zapobiec nieuprawnionemu dostępowi do nośników danych, w
            szczególności poprzez ustawienie haseł, ograniczenie dostępu, szyfrowanie oraz zastosowanie innych środków
            technicznych i organizacyjnych.
          </li>
          <li>
            Administrator nie ponosi jednak odpowiedzialności za udostępnienie przez użytkownika danych logowania do
            konta lub logowania i korzystania z Portalu na urządzeniach nienależących do użytkownika, dzięki czemu
            potencjalnie inne osoby mogą mieć dostęp do danych osobowych użytkownika.
          </li>
        </ul>

        <h2>Przekazywanie danych osobowych</h2>
        <p>
          Dane osobowe przekazujemy osobom trzecim wyłącznie w przypadkach przewidzianych przepisami prawa (np. w
          zakresie obowiązkowych zgłoszeń do właściwych organów państwowych) lub w niezbędnym zakresie wybranym
          dostawcom świadczącym dla nas określone usługi, takie jak: prowadzenie ksiąg rachunkowych, administracja
          technologiami informatycznymi (IT) czy korzystanie z bramek płatniczych do przetwarzania płatności za usługi.
          Ze wszystkimi takimi podmiotami posiadamy stosowne umowy w zakresie powierzenia przetwarzania danych
          osobowych, a wszyscy dostawcy przestrzegają niezbędnych zasad przetwarzania danych osobowych w zakresie
          określonym przez RODO i zgodnie z wymogami tam wskazanymi.
        </p>
        <p>
          Dostawcy usług mają siedzibę głównie w Polsce, Czechach i w innych krajach Europejskiego Obszaru
          Gospodarczego.
        </p>
        <p>
          Jako Administrator mamy również prawo, a także ustawowy obowiązek, do przekazania wybranych bądź wszystkich
          informacji dotyczących użytkowników organom władzy publicznej bądź podmiotom trzecim, które zgłoszą takie
          żądanie udzielenia informacji na podstawie obowiązujących przepisów prawa.
        </p>
        <p>
          W uzasadnionych przypadkach możemy udostępnić Twoje dane osobowe podmiotom mającym siedzibę poza EOG, tj. np.
          USA, Singapur, Indie, Chiny, Hong Kong i Kanada, do których transfer jest niezbędny w zakresie celów
          wskazanych niniejszej Polityce Prywatności. Przekazanie danych osobowych poza EOG następuje z zapewnieniem
          odpowiedniego poziomu ochrony danych, wymaganego przez przepisy europejskie, przede wszystkim poprzez:
        </p>
        <ol>
          <li>
            współpracę z podmiotami przetwarzającymi dane osobowe w państwach, w odniesieniu do których została wydana
            stosowna decyzja Komisji Europejskiej;
          </li>
          <li>stosowanie standardowych klauzul umownych wydanych przez Komisję Europejską;</li>
          <li>stosowanie wiążących reguł korporacyjnych, zatwierdzonych przez właściwy organ nadzorczy;</li>
          <li>
            inne zabezpieczenia spełniające odpowiedni stopień ochrony w oparciu o odpowiednie zabezpieczenia prawne.
          </li>
        </ol>
        <p>
          Masz prawo do uzyskania kopii ww. standardowych klauzul umownych (lub innych stosownych zabezpieczeń transferu
          danych poza EOG) poprzez kontakt z Administratorem.
        </p>

        <h2>Prawa osób, których dane dotyczą</h2>
        <ul>
          <li>
            W związku z przetwarzaniem Twoich danych osobowych przysługują Ci prawa, z których możesz skorzystać w
            dowolnym momencie. Obejmuje to w szczególności prawo do:
            <ol>
              <li>dostępu do danych osobowych (informacji o przetwarzaniu danych osobowych);</li>
              <li>uzyskania kopii danych;</li>
              <li>sprostowania danych osobowych;</li>
              <li>usunięcia danych osobowych (prawo do bycia zapomnianym);</li>
              <li>ograniczenia przetwarzania;</li>
              <li>przenoszenia danych;</li>
              <li>sprzeciwu wobec przetwarzania;</li>
              <li>sprzeciwu wobec przetwarzania danych osobowych na potrzeby marketingu bezpośredniego;</li>
              <li>
                wycofania zgody na przetwarzanie danych osobowych, jeżeli przetwarzanie odbywa się na podstawie zgody;
              </li>
              <li>wniesienia skargi do organu nadzorczego.</li>
            </ol>
          </li>
          <li>
            W przypadku naruszenia bezpieczeństwa danych osobowych, pomimo wszystkich opisanych środków, dysponujemy
            systemem zgłaszania wszelkich incydentów bezpieczeństwa. W przypadku jakiegokolwiek naruszenia
            bezpieczeństwa danych osobowych postępujemy zgodnie z RODO w celu minimalizacji ewentualnych szkód i
            składamy stosowne zgłoszenia do właściwego Urzędu Ochrony Danych Osobowych lub innego uprawnionego organu.
          </li>
          <li>
            W przypadku pytań dotyczących zakresu i sposobu przetwarzania danych osobowych prosimy o kontakt pod adresem
            <a href="mailto:sufler@dramox.pl">sufler@dramox.pl</a> lub na adres Administratora wskazany powyżej.
          </li>
        </ul>

        <h2>Profilowanie</h2>
        <p>
          Dane osobowe takie jak: imię, nazwisko, adres e-mail oraz numer telefonu, po wyrażeniu dobrowolnej zgody przez
          użytkownika, zbierane są i przetwarzane także w celach statystycznych i marketingowych („Profilowanie”).
        </p>
        <p>
          Za pomocą narzędzi do profilowania danych osobowych Administrator jest w stanie dokonać oceny osobowych
          czynników dotyczących konkretnego użytkownika, a w szczególności analizować lub prognozować aspekty dotyczące
          osobistych preferencji, zainteresowań lub zachowań.
        </p>
        <p>
          Profilowanie dokonywane jest w celu dostarczenia indywidualnym użytkownikom spersonalizowanych i dopasowanych
          ofert oraz treści, m. in. poprzez dynamiczne treści na stronie Sklepu Internetowego, czy też przez e-mail
          marketing.
        </p>
        <p>
          Profilowanie danych osobowych użytkownika odbywa się na podstawie wyrażonej uprzednio dobrowolnej zgody
          użytkownika.
        </p>
        <p>
          Użytkownik ma prawo do cofnięcia zgody na przetwarzanie jego danych osobowych polegające na profilowaniu, a
          także prawo do zgłoszenia sprzeciwu względem profilowania poprzez kontakt z Administratorem.
        </p>
        <p>
          Administrator zastrzega sobie prawo do modyfikacji niniejszej Polityki Prywatności w przypadku zmiany
          przepisów prawa lub okoliczności opisanych w dokumencie – o każdej takiej zmianie użytkownicy zostaną
          poinformowani zgodnie z obowiązującymi przepisami i z odpowiednim wyprzedzeniem.
        </p>

        <p>Niniejsza Polityka Prywatności obowiązuje od dnia 1.11.2022.</p>
      </>
    );
  }, []);

  const getTerms = useCallback(() => {
    switch (locale) {
      case 'cs':
        return policyCs();
      case 'sk':
        return policySk();
      case 'uk':
        return policeUk();
      case 'pl':
        return policyPl();
      default:
        return policyEn();
    }
  }, [locale, policyCs, policeUk, policyEn, policyPl]);

  return (
    <Layout countryCode={countryCode} hiddenWrappers={hideHeaderAndFooter} allowPopups={false}>
      <Head>{basicSEO(t('seo:privacy_policy_title'), t('seo:privacy_policy_description'))}</Head>
      <main className={[styles.legal, hideLinks ? styles.hideLinks : null].join(' ')}>{getTerms()}</main>
    </Layout>
  );
};

 async function _getServerSideProps(
  ctx: GetServerSidePropsContext<StaticPathParams>,
): Promise<GetServerSidePropsResult<Props>> {
  const { countryCode } = await authorizeUserServerSide(ctx);
  const forceLocale = (ctx.query?.locale as string) ?? null;

  return { props: { countryCode, forceLocale } };
}

export default PrivacyPolicy;


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/privacy-policy/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  